import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { Button } from '../ui/button'
import { Icon, IconName } from '../ui/icon'
import Logo from '../ui/logo'
import { useTheme } from '../../contexts/ThemeContext'

type SidebarProps = {
	onClose: () => void
	isAdmin?: boolean
}

const Sidebar = ({ onClose, isAdmin = false }: SidebarProps) => {
	const location = useLocation()
	const { user, hasRole, getUserType } = useAuth()
	const { theme } = useTheme()

	// Define navigation items based on role
	const baseAdminItems = [
		{ label: 'Dashboard', path: '/admin', icon: 'home' as IconName },
		// { label: 'Users', path: '/admin/users', icon: 'avatar' as IconName },
	]

	// Add Administrator Management only for default-administrators
	const adminManagementItem = {
		label: 'Administrator Management',
		path: '/admin/administrators',
		icon: 'user' as IconName,
	}

	// const shouldShowAdminManagement = getUserType() === 'default-administrator' || hasRole('default-administrator')
	const shouldShowAdminManagement = true;

	const restAdminItems = [
		{
			label: 'Passenger Management',
			path: '/admin/passengers',
			icon: 'user-group' as IconName,
		}, // Multiple regular users
		{
			label: 'Driver Management',
			path: '/admin/drivers',
			icon: 'car' as IconName,
		}, // Car represents drivers
		{
			label: 'Ride Management',
			path: '/admin/rides',
			icon: 'map' as IconName,
		}, // Map for tracking rides
		{ label: 'Pricing Model', path: '/admin/pricing', icon: 'car' as IconName },
		{
			label: 'Financial Management',
			path: '/admin/finance',
			icon: 'landmark' as IconName,
		},
		// { label: 'In-wallet details', path: '/admin/wallet', icon: 'wallet' as IconName },
		{ label: 'Wallets', path: '/admin/wallets', icon: 'wallet' as IconName },
		{ label: 'IPRS Data', path: '/admin/iprs', icon: 'database' as IconName },
		{
			label: 'Ratings and feedback',
			path: '/admin/ratings',
			icon: 'star' as IconName,
		}, // Star for ratings
		{
			label: 'Communications',
			path: '/admin/communications',
			icon: 'messages-square' as IconName,
		},
		// {
		// 	label: 'Promotional Management',
		// 	path: '/admin/promotions',
		// 	icon: 'megaphone',
		// }, // Megaphone for promotions

		{ label: 'Settings', path: '/admin/settings', icon: 'cog' as IconName },
	]

	const navItems = isAdmin
		? shouldShowAdminManagement 
			? [...baseAdminItems, adminManagementItem, ...restAdminItems]
			: [...baseAdminItems, ...restAdminItems]
		: [
				{
					label: 'Dashboard',
					path: '/dashboard',
					icon: 'dashboard' as IconName,
				},
				{ label: 'Profile', path: '/profile', icon: 'user' as IconName },
				{ label: 'Settings', path: '/settings', icon: 'cog' as IconName },
			]

	// Helper function to render the icon
	const renderIcon = (icon: string) => {
		switch (icon) {
			case 'dashboard':
				return (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						className="mr-3 h-5 w-5"
					>
						<rect x="3" y="3" width="7" height="7" />
						<rect x="14" y="3" width="7" height="7" />
						<rect x="14" y="14" width="7" height="7" />
						<rect x="3" y="14" width="7" height="7" />
					</svg>
				)
			// Add other icons as needed
			default:
				return (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						className="mr-3 h-5 w-5"
					>
						<circle cx="12" cy="12" r="10" />
					</svg>
				)
		}
	}

	return (
		<div className="flex h-full flex-col bg-card">
			<div className="flex h-16 items-center justify-between border-b px-4">
				<Logo
					key={theme} // Adding this key forces re-render when theme changes
					src={
						theme == 'dark'
							? '/images/Ubuntu_cabwhite.png'
							: '/images/ubuntucab_green.png'
					}
					alt="Ubuntucabs"
					className="logo-class h-12 w-36"
				/>
				<Button
					variant="ghost"
					size="icon"
					onClick={onClose}
					className="md:hidden"
					aria-label="Close sidebar"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<line x1="18" y1="6" x2="6" y2="18" />
						<line x1="6" y1="6" x2="18" y2="18" />
					</svg>
				</Button>
			</div>

			<div className="flex-1 overflow-y-auto py-4">
				<nav className="px-2">
					<ul className="space-y-2">
						{navItems.map(item => (
							<li key={item.path}>
								<Link
									to={item.path}
									className={`flex items-center rounded-md px-3 py-2 text-sm transition-colors  ${
										location.pathname === item.path
											? 'bg-primary text-primary-foreground hover:bg-primary/90'
											: 'text-foreground hover:bg-muted'
									}`}
									onClick={onClose}
								>
									<Icon name={item.icon} className="mr-3 h-5 w-5" />
									{item.label}
								</Link>
							</li>
						))}
					</ul>
				</nav>
			</div>

			<div className="border-t p-4">
				<div className="flex items-center">
					<div className="ml-2">
						<p className="text-sm font-medium">{user?.username || 'User'}</p>
						<p className="text-xs text-muted-foreground">{user?.email || ''}</p>
					</div>
				</div>
			</div>
		</div>
	)
}

export default Sidebar
